import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AREAS, C, CONV_OPTS } from "./constants";
import { PrivacyNote } from "./PrivacyNote";
import { PerceptionByArea } from "./PerceptionPanel";

const GUION = [
  "¿Lo que se cuenta fuera sobre cómo es trabajar aquí coincide con lo que vives día a día?",
  "Cuando entra alguien nuevo, ¿suele encajar bien con lo que el puesto necesita?",
  "Piensa en tu incorporación o en la de alguien reciente: ¿hubo un acompañamiento claro en las primeras semanas?",
  "¿Sabes con claridad en qué te evalúan y qué necesitas para hacerlo bien?",
  "¿Con qué frecuencia recibes feedback útil, más allá de una evaluación formal?",
  "¿Ves un camino claro de crecimiento para ti dentro de esta empresa?",
];
const KIND_BY_VALUE = { coincide: "coincidencia", menos: "falsa_alarma", mas: "punto_ciego" };

const Guide = () => (
  <div className="space-y-4 text-sm" style={{ color: C.text }} data-testid="tablero-conversaciones-guia">
    <p>Con un equipo pequeño no se puede garantizar el anonimato, así que no recogemos respuestas. Te proponemos conversaciones individuales de 20 a 30 minutos con cada persona del equipo.</p>
    <p>Explica para qué lo haces, que nadie sufrirá consecuencias por lo que diga y que solo compartirás temas generales, nunca quién dijo qué. Después de cada conversación, anota temas por área, nunca nombres ni frases literales.</p>
    <ol className="list-decimal pl-5 space-y-1">{GUION.map((q) => <li key={q}>{q}</li>)}</ol>
    <p><span className="font-semibold">Para cerrar:</span> ¿Qué cambiarías de cómo se gestiona el talento aquí? ¿Qué le dirías a la dirección sin ningún filtro?</p>
    <div className="rounded-lg bg-white border p-4" style={{ borderColor: "#E3E7E1" }}>
      <p className="font-semibold">Alternativa: retrospectiva de equipo (45 minutos)</p>
      <p className="mt-1">10 minutos para explicar las reglas (no se señala a personas); 25 minutos en los que cada persona escribe en notas anónimas lo que funciona y lo que duele por área, y se agrupan; 10 minutos de votación, con tres puntos por persona, de las áreas a cambiar primero.</p>
    </div>
  </div>
);

export const ConversacionesGuiadas = ({ testId, saved, onSaved }) => {
  const [conv, setConv] = useState(saved || {});
  const [saving, setSaving] = useState(false);
  const set = (code, patch) => setConv((c) => ({ ...c, [code]: { nota: "", ...c[code], ...patch } }));
  const done = Object.keys(saved || {}).length > 0;

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/consulting/tests/${testId}/tablero/capa2/conversaciones`, { conversaciones: conv });
      onSaved(r.data);
      toast.success("Síntesis guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos guardar la síntesis");
    } finally {
      setSaving(false);
    }
  }

  const byArea = Object.fromEntries(Object.entries(saved || {}).map(([a, v]) => [a, { kind: KIND_BY_VALUE[v.valor], label: CONV_OPTS.find((o) => o.v === v.valor)?.l, nota: v.nota }]));

  return (
    <div className="space-y-5" data-testid="tablero-conversaciones">
      <Guide />
      <div className="space-y-3">
        <p className="text-sm font-semibold" style={{ color: C.text }}>Síntesis por área</p>
        {AREAS.map((a) => (
          <div key={a.code} className="rounded-lg bg-white border p-3 space-y-2" style={{ borderColor: "#E3E7E1" }} data-testid={`tablero-conv-row-${a.code}`}>
            <div className="text-sm font-semibold" style={{ color: C.text }}>{a.name}</div>
            <div className="flex flex-wrap gap-2">
              {CONV_OPTS.map((o) => {
                const active = conv[a.code]?.valor === o.v;
                return (
                  <button key={o.v} type="button" onClick={() => set(a.code, { valor: o.v })} className="rounded-full border px-3 py-1.5 text-[12px] font-semibold" style={active ? { background: C.green, color: "#fff", borderColor: C.green } : { color: C.text, borderColor: "#CBD5CF" }} data-testid={`tablero-conv-${a.code}-${o.v}`}>
                    {o.l}
                  </button>
                );
              })}
            </div>
            <input value={conv[a.code]?.nota || ""} maxLength={200} disabled={!conv[a.code]?.valor} onChange={(e) => set(a.code, { nota: e.target.value })} placeholder="Nota opcional: temas generales, sin nombres (máximo 200 caracteres)" className="w-full rounded-lg border px-3 py-2 text-sm disabled:bg-slate-50" style={{ borderColor: "#CBD5CF" }} data-testid={`tablero-conv-nota-${a.code}`} />
            <PrivacyNote testid={`tablero-privacy-note-conv-${a.code}`} />
          </div>
        ))}
        <button onClick={save} disabled={saving || !Object.keys(conv).length} className="rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-40" style={{ background: C.green }} data-testid="tablero-conv-save">
          Guardar síntesis
        </button>
      </div>
      {done && <PerceptionByArea byArea={byArea} note="Basado en tus conversaciones, no en datos anónimos del equipo." />}
    </div>
  );
};
